import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PersonApi } from '../+api';
import {
  ContextInterface,
  EduContentInterface,
  MixedResultInterface,
  ResultInterface,
  ResultStatusEnum,
} from '../+models';
import { BearerHttpService } from '../bearer-http';
import { DAL_OPTIONS, DalOptions } from '../dal.module';
import {
  BookListScoreInterface,
  EvaluationDetailInterface,
  ResultFiltersInterface,
  ResultListItemInterface,
  ResultsServiceInterface,
} from './results.service.interface';

@Injectable({
  providedIn: 'root',
})
export class ResultsService implements ResultsServiceInterface {
  private bearerHttpService = inject(BearerHttpService);
  private dalOptions = inject<DalOptions>(DAL_OPTIONS);
  private campusApiBaseUrl = this.dalOptions.campusApiBaseUrl;

  constructor(private personApi: PersonApi) {}

  public getResultsForStudentDetail<T = any>(
    studentId: number,
    filters: ResultFiltersInterface
  ): Observable<{ results: MixedResultInterface<T>[]; eduContents: EduContentInterface[] }> {
    const params = new HttpParams({
      fromObject: {
        filters: JSON.stringify(filters),
      },
    });
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/students/${studentId}`, params);
  }

  public getResultsForToc(
    tocId: number
  ): Observable<{ tocId: number; overview: any[]; eduContents: EduContentInterface[] }> {
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/lessons/${tocId}`);
  }

  public getResultsForFluo(): Observable<{ results: ResultInterface[]; eduContents: EduContentInterface[] }> {
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/curriculum/fluo`);
  }

  public getResultsForAdaptiveToc(tocId: number): Observable<{
    results: Dictionary<Dictionary<Dictionary<ResultInterface[]>>>;
    eduContents: EduContentInterface[];
  }> {
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/adaptive/${tocId}`);
  }

  public getResultsForTeacherOverview<T = any>(
    filters: ResultFiltersInterface
  ): Observable<ResultListItemInterface<T>[]> {
    const params = new HttpParams({
      fromObject: {
        filters: JSON.stringify(filters),
      },
    });
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/students`, params);
  }

  public getTasksSummaryForTeacher<T = any>(filters: ResultFiltersInterface): Observable<ResultListItemInterface<T>[]> {
    const params = new HttpParams({
      fromObject: {
        filters: JSON.stringify(filters),
      },
    });
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/tasks`, params);
  }

  public getBundlesSummaryForTeacher<T = any>(
    filters: ResultFiltersInterface
  ): Observable<ResultListItemInterface<T>[]> {
    const params = new HttpParams({
      fromObject: {
        filters: JSON.stringify(filters),
      },
    });
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/bundles`, params);
  }

  public getEvaluationsSummaryForTeacher(bookId: number): Observable<Dictionary<BookListScoreInterface>> {
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/evaluations/${bookId}`);
  }

  public getResultsForTask<T = any>(
    taskId: number,
    filters: ResultFiltersInterface = {}
  ): Observable<{
    results: MixedResultInterface<T>[];
    algebraKitSessions: MixedResultInterface<T>[];
    eduContents: EduContentInterface[];
  }> {
    const params = new HttpParams({
      fromObject: {
        filters: JSON.stringify(filters),
      },
    });
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/tasks/${taskId}`, params);
  }

  public getResultsForBundle<T = any>(
    bundleId: number,
    filters: ResultFiltersInterface = {}
  ): Observable<{
    results: MixedResultInterface<T>[];
    algebraKitSessions: MixedResultInterface<T>[];
    eduContents: EduContentInterface[];
  }> {
    const params = new HttpParams({
      fromObject: {
        filters: JSON.stringify(filters),
      },
    });
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/bundles/${bundleId}`, params);
  }

  public getResultsForEvaluationDetail(scoreListId: number): Observable<EvaluationDetailInterface> {
    return this.bearerHttpService.get(`${this.campusApiBaseUrl}/results/evaluations/scoreList/${scoreListId}`);
  }

  getAllForUser(userId: number): Observable<ResultInterface[]> {
    return this.personApi
      .getData(userId, 'allResults')
      .pipe(map((res: { allResults: ResultInterface[] }) => res.allResults.map(castResult)));
  }

  public getResultForReview(userId: number, resultId: number): Observable<ResultInterface> {
    return this.personApi.resultForReview(userId, resultId).pipe(map(castResult));
  }

  /**
   * Checks the polpo-api if a result exists and returns it if it is in progress.
   * Creates a new result if it doesn't exist and returns it.
   * Returns an error if a result is completed and the user isn't allowed to repeat it.
   *
   * @param {number} userId
   * @param {number} taskId
   * @param {number} eduContentId
   * @returns {Observable<ResultInterface>}
   * @memberof ResultsService
   */
  public getResultForTask(userId: number, taskId: number, eduContentId: number): Observable<ResultInterface> {
    return this.personApi.resultForTask(userId, taskId, eduContentId).pipe(map(castResult));
  }

  /**
   * Checks the polpo-api if a result exists for unlockedContent and returns it if it is in progress.
   * Creates a new result if it doesn't exist (or is completed) and returns it.
   *
   */
  public getResultForUnlockedContent(
    userId: number,
    unlockedContentId: number,
    eduContentId: number
  ): Observable<ResultInterface> {
    return this.personApi.resultForUnlockedContent(userId, unlockedContentId, eduContentId).pipe(map(castResult));
  }

  /**
   * Checks the polpo-api if a result exists for unlockedFreePractice and returns it if it is in progress.
   * Creates a new result if it doesn't exist (or is completed) and returns it.
   *
   */
  public getResultForUnlockedFreePractice(
    userId: number,
    unlockedFreePracticeId: number,
    eduContentId: number
  ): Observable<ResultInterface> {
    return this.personApi
      .resultForUnlockedFreePractice(userId, unlockedFreePracticeId, eduContentId)
      .pipe(map(castResult));
  }

  /**
   * Checks the polpo-api if a result exists for context parameters and returns it if it is in progress.
   * Fails if it doesn't exist or is already completed.
   *
   */
  public getResultForContext(
    userId: number,
    eduContentId: number,
    context: ContextInterface
  ): Observable<ResultInterface> {
    return this.personApi.resultForContext(userId, eduContentId, context).pipe(map(castResult));
  }

  /**
   * Saves a result to the Api, returns the saved result
   */
  public saveResult(userId: number, result: ResultInterface): Observable<ResultInterface> {
    const standardizedResult = standardizeResult(result);

    return this.personApi
      .saveResult(
        userId,
        standardizedResult.id,
        standardizedResult.score,
        standardizedResult.time,
        standardizedResult.status,
        standardizedResult.cmi
      )
      .pipe(map(castResult));
  }
}

function standardizeResult(result: ResultInterface): ResultInterface {
  return {
    ...result,
    score: standardizeScore(result.score),
    status: standardizeStatus(result.status),
  };
}

function standardizeScore(score: number) {
  return score ? +score : score;
}

function standardizeStatus(status: ResultStatusEnum) {
  if ([ResultStatusEnum.STATUS_PASSED, ResultStatusEnum.STATUS_FAILED].includes(status)) {
    return ResultStatusEnum.STATUS_COMPLETED;
  }

  return status;
}

function castResult(result: ResultInterface) {
  return {
    ...result,
    created: result.created && new Date(result.created),
    lastUpdated: result.lastUpdated && new Date(result.lastUpdated),
  };
}
